import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { TaskAgenda, TaskAgendaRun } from '@/types/taskAgenda';
import { parseDailyTimesSpInput } from '@/services/taskAgenda/taskAgendaSchedule';
import { getRotinaSistema, type TaskAgendaAutomationTipo } from '@/config/taskAgendaRotinas';

export const TASK_AGENDA_QUERY_KEY = ['task-agenda'] as const;
export const taskAgendaRunsQueryKey = (agendaId: string) => ['task-agenda-runs', agendaId] as const;

export function taskAgendaByTipoQueryKey(tipo: string) {
  return ['task-agenda-tipo', tipo] as const;
}

export function useTaskAgendaList() {
  return useQuery({
    queryKey: TASK_AGENDA_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_agenda')
        .select('*')
        .neq('tipo', 'diversos')
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data || []) as TaskAgenda[];
    },
  });
}

/**
 * Primeira linha da agenda para o tipo; se não existir, cria registro padrão (idempotente em condição normal).
 */
export function useTaskAgendaByAutomationTipo(tipo: TaskAgendaAutomationTipo | undefined) {
  return useQuery({
    queryKey: taskAgendaByTipoQueryKey(tipo || ''),
    enabled: Boolean(tipo),
    queryFn: async () => {
      const { data: row, error } = await supabase
        .from('task_agenda')
        .select('*')
        .eq('tipo', tipo!)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (row) return row as TaskAgenda;

      const r = getRotinaSistema(tipo);
      const cat = r?.permiteAgendamentoNoApp ? r : undefined;
      if (!cat) throw new Error('Tipo de rotina inválido ou sem agendamento no aplicativo');
      const slots = parseDailyTimesSpInput(cat.horarioPadraoTexto);
      if (!slots?.length) throw new Error('Horário padrão inválido no catálogo');
      const [h0, m0] = slots[0].split(':').map((x) => Number(x));
      const payload: Record<string, unknown> = { ...cat.insertPayload };
      if (slots.length > 1) payload.dailyTimesSp = slots;

      const insertRow = {
        titulo: cat.tituloPadrao,
        descricao: cat.descricaoCurta,
        tipo,
        ativo: true,
        schedule_kind: 'daily_sp' as const,
        schedule_hour_sp: h0,
        schedule_minute: m0,
        schedule_weekdays: null as number[] | null,
        data_inicio: null as string | null,
        data_fim: null as string | null,
        payload,
      };

      const { data: ins, error: insErr } = await supabase.from('task_agenda').insert(insertRow).select('*').single();
      if (insErr) {
        const { data: again, error: e2 } = await supabase
          .from('task_agenda')
          .select('*')
          .eq('tipo', tipo!)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        if (!e2 && again) return again as TaskAgenda;
        const msg = insErr.message || '';
        if (msg.includes('task_agenda_tipo_check') || msg.includes('violates check constraint')) {
          throw new Error(
            'O banco ainda não aceita o tipo "chamados_sync". Execute a migração supabase/migrations/20260526130000-task-agenda-chamados-sync-tipo.sql no SQL Editor do Supabase.'
          );
        }
        throw insErr;
      }
      return ins as TaskAgenda;
    },
  });
}

export function useTaskAgendaRuns(agendaId: string | null) {
  return useQuery({
    queryKey: taskAgendaRunsQueryKey(agendaId || ''),
    enabled: Boolean(agendaId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_agenda_runs')
        .select('*')
        .eq('agenda_id', agendaId!)
        .order('started_at', { ascending: false })
        .limit(80);
      if (error) throw error;
      return (data || []) as TaskAgendaRun[];
    },
  });
}

export function useUpdateTaskAgenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<TaskAgenda> & { id: string }) => {
      const { id, ...rest } = patch;
      const { error } = await supabase.from('task_agenda').update(rest).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TASK_AGENDA_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ['task-agenda-tipo'] });
    },
  });
}
